import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ENDPOINTS from '../../config/apiUrls';
import axiosInstance, { setAuthToken } from '../../config/axiosInstance';
import { postRequest } from '../../config/dataApi';
import { toast } from "react-toastify";
import { download } from './fileSlice';
import readBlobs from '../../helpers/readBlobs';
import { filterNonMatchingValues } from '../../helpers/filterObject';
import { getUserRoleInfo } from '../../helpers/roleUtils';
import { purgeCloudflareCache } from '../../helpers/cloudflareCache';
import moment from 'moment';
import { getToken } from '../../helpers/utils';
import { createAction } from '@reduxjs/toolkit';

const OTHER_USER_STORAGE_KEY = 'authOtherUser';

const initialState = {
    isAuthenticated: false, // Whether the user is authenticated or not
    user: null, // The user data (current acting user)
    otherUser: null, // The group_admin user when they logged in; kept so they can switch station
    token: localStorage.getItem('authToken') || null,
    apiLimit: null,
    loading: false, // Whether login is in progress
    error: null, // Error message if any
    isRoot: false,
    userRoleInfo: {
        isRoot: false,
        isAdmin: false,
        isDJ: false,
        isGuest: false,
        roles: [],
        primaryRole: 'guest'
    },
};

export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async ({ username, password }, { rejectWithValue }) => {
        const requestData = {
            data: {
                type: 'user',
                id: username,
                attributes: {
                    password: password,
                },
            },
        };

        try {
            const response = await postRequest(ENDPOINTS.ADMIN.LOGIN, requestData);
            if (response.meta) {

                setAuthToken(response.meta.jwt);

                return response;

            } else {

                return rejectWithValue('Invalid username or password.');

            }
        } catch (err) {
            return rejectWithValue(err.response ? err.response.message : 'An error occurred.');
        }
    }
);

export const regeneratePassword = createAsyncThunk(
    'auth/regeneratePassword',
    async ({ email }, { rejectWithValue }) => {
        const requestData = {
            data: {
                type: 'user',
                id: email,
            },
        };

        try {
            const response = await postRequest(ENDPOINTS.ADMIN.REGENERATE_PASSWORD, requestData);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response ? err.response.message : 'An error occurred.');
        }
    }
);

export const fetchCurrentUserProfile = createAsyncThunk('auth/fetchCurrentUserProfile', async (token, { rejectWithValue }) => {



    const response = await axiosInstance.get(ENDPOINTS.ADMIN.PROFILE, {
        headers: { Authorization: `JWT ${token}` },
    });
    if (response.data.data) {
        return response.data.data;
    } else {
        return rejectWithValue('Invalid username or password.');
    }
});

export const impersonateStationUser = createAsyncThunk(
    'auth/impersonateStationUser',
    async (row, { rejectWithValue, dispatch }) => {
        try {
             const requestData = {
            data: {
                type: 'user',
                id: row?.attributes?.username,
                attributes: {
                    password: 'test@124',
                },
            },
        };  
            let groupAdminToken = localStorage.getItem('groupAdminAuthToken');
            if(groupAdminToken){
               setAuthToken(groupAdminToken);
            }
            const response = await axiosInstance.post(ENDPOINTS.ADMIN.IMPERSONATE,requestData);
            const jwt = response.data;
            if (!jwt) {
                return rejectWithValue('No token returned from impersonation.');
            }
            setAuthToken(jwt);
            localStorage.setItem('authToken', jwt);
            sessionStorage.setItem('authToken', jwt);
            const profileResponse = await dispatch(fetchCurrentUserProfile(jwt)).unwrap();
            return { user: profileResponse, stationId : row?.id };
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.errors?.[0]?.detail || err.response?.data?.message || err.message || 'Impersonation failed.'
            );
        }
    }
);

export const updateTwitter = createAsyncThunk('auth/updateTwitter', async (token, { rejectWithValue }) => {

    try {
        const response = await axiosInstance.delete(ENDPOINTS.SOCIAL.UPDATE_TWITTER);
        if (response.status === 204) {
            toast.success("Twitter account disconnected successfully.", {
                position: "top-right",
                autoClose: 3000,
            });

            return true;
        } else {
            return rejectWithValue('Failed to update profile.');
        }
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
});

export const connectTwitter = createAsyncThunk('auth/connectTwitter', async (twitterData, { rejectWithValue, dispatch, getState }) => {
    try {
        const response = await axiosInstance.put(ENDPOINTS.USERS.UPDATE_PROFILE, {
            data: {
                type: 'user',
                attributes: {
                    twitter: twitterData
                }
            }
        });
        const token = getState().auth.token;
        if (token) {
            await dispatch(fetchCurrentUserProfile(token));
        }
        if (response.status === 200) {
            toast.success("Twitter account connected successfully.", {
                position: "top-right",
                autoClose: 3000,
            });
            return twitterData;
        } else {
            return rejectWithValue('Failed to connect Twitter account.');
        }
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
});

export const apiLimitSetByAdmin = createAsyncThunk('auth/apiLimitSetByAdmin', async (token, { rejectWithValue }) => {
    const response = await axiosInstance.get(ENDPOINTS.ADMIN.API_LIMIT, {
        headers: { Authorization: `JWT ${token}` },
    });
    if (response.data.data) {

        let data = response.data.data;
        if (data && data.loginConfiguration) {
            const { hour, date } = data.loginConfiguration;
            const [hourPart, minutePart = '00'] = hour.split(':');
            const utcTime = `${date} ${hourPart}:${minutePart}:00`; // Format: 'YYYY-MM-DD HH:mm:ss'
            const timezone = moment.tz.guess();
            const localTime = moment.tz(utcTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').tz(timezone);
            const startTime = localTime.format('hh:mm A'); // Exact hour and minutes
            const endTime = localTime.clone().add(1, 'hour').format('hh:mm A'); // 1 hour later

            data.loginConfiguration.startTime = startTime;
            data.loginConfiguration.endTime = endTime;
            data.loginConfiguration.timezone = timezone;
        }

        return data;
    } else {
        return rejectWithValue('Invalid username or password.');
    }
});

export const updateApiLimit = createAsyncThunk(
    'auth/updateApiLimit',
    async ({ message = 'Limit updated successfully', values }, { rejectWithValue, getState }) => {
        try {
            const response = await axiosInstance.post(ENDPOINTS.ADMIN.UPDATE_API_LIMIT, { limit: parseInt(values.limit) });

            if (response.status === 204) {
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                });

                return values;
            } else {
                return rejectWithValue('Failed to update profile.');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'An error occurred.');
        }
    }
);

export const updateUserProfile = createAsyncThunk(
    'auth/updateUserProfile',
    async ({ message = 'Profile updated successfully', values, isForStation = false, targetGroupAdmin = false }, { rejectWithValue, getState }) => {
        try {
            const { auth } = getState();
            const targetUser = targetGroupAdmin ? (auth?.otherUser || auth?.user) : auth?.user;
            const username = isForStation ? auth?.user?.attributes?.ownerEmail : targetUser?.attributes?.username;
            const url = isForStation ? ENDPOINTS.USERS.UPDATE_STATION_INFO : ENDPOINTS.USERS.UPDATE_PROFILE;
            const thingsToUpdate = await filterNonMatchingValues(values, targetUser?.attributes);
            const rawGroupAdminToken = targetGroupAdmin
                ? (localStorage.getItem('groupAdminAuthToken') || sessionStorage.getItem('groupAdminAuthToken'))
                : null;
            const groupAdminToken = (rawGroupAdminToken && rawGroupAdminToken !== 'undefined' && rawGroupAdminToken !== 'null')
                ? rawGroupAdminToken
                : null;
            if (targetGroupAdmin && !groupAdminToken) {
                return rejectWithValue("Group admin token missing. Please login again.");
            }

            if (Object.keys(thingsToUpdate).length === 0) {
                return false;
            }
            if (!username) {
                return rejectWithValue("User not found.");
            }
            const requestData = {
                data: {
                    type: 'user',
                    id: username,
                    attributes: thingsToUpdate
                },
            };

            const response = await axiosInstance.put(
                url,
                requestData,
                groupAdminToken ? { headers: { Authorization: `JWT ${groupAdminToken}` } } : undefined
            );

            if (response.status === 204) {
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                });

                // Purge Cloudflare cache for users/list URL with stationGroup
                const { stationGroup } = targetUser?.attributes || {};
                if (stationGroup) {
                    await purgeCloudflareCache(stationGroup);
                }

                return {
                    ...requestData.data.attributes,
                    __targetGroupAdmin: targetGroupAdmin,
                };
            } else {
                return rejectWithValue('Failed to update profile.');
            }
        } catch (error) {
            console.log(error.message)
            return rejectWithValue(error.response?.data?.message || 'An error occurred.');
        }
    }
);

export const updateVideoAds = createAsyncThunk(
    'auth/updateVideoAds',
    async ({ message, values, isForStation = false }, { rejectWithValue, getState }) => {
        try {
            const { auth } = getState();
            const username = isForStation ? auth?.user?.attributes?.ownerEmail : auth.user?.attributes?.username;
            const url = isForStation ? ENDPOINTS.USERS.UPDATE_STATION_INFO : ENDPOINTS.USERS.UPDATE_PROFILE;
            console.log(values, 'values')
            const requestData = {
                data: {
                    type: 'user',
                    id: username,
                    attributes: values
                },
            };
            const response = await axiosInstance.put(url, requestData);

            if (response.status === 204) {
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                });
                const { stationGroup } = getState().auth.user.attributes;
                if (stationGroup) {
                    await purgeCloudflareCache(stationGroup);
                }
                return values;
            } else {
                return rejectWithValue('Failed to update profile.');
            }
        } catch (error) {
            console.log(error.message)
            return rejectWithValue(error.response?.data?.message || 'An error occurred.');
        }
    }
);

export const updateAudioAds = createAsyncThunk(
    'auth/updateAudioAds',
    async ({ message, values, isForStation = false  }, { rejectWithValue, getState }) => {
        try {
            const { auth } = getState();
            const username = isForStation ? auth?.user?.attributes?.ownerEmail : auth.user?.attributes?.username;
            const url = isForStation ? ENDPOINTS.USERS.UPDATE_STATION_INFO : ENDPOINTS.USERS.UPDATE_PROFILE;
            const requestData = {
                data: {
                    type: 'user',
                    id: username,
                    attributes: values
                },
            };
            const response = await axiosInstance.put(url, requestData);

            if (response.status === 204) {
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                });
                const { stationGroup } = getState().auth.user.attributes;
                if (stationGroup) {
                    await purgeCloudflareCache(stationGroup);
                }

                return values;
            } else {
                return rejectWithValue('Failed to update profile.');
            }
        } catch (error) {
            console.log(error.message)
            return rejectWithValue(error.response?.data?.message || 'An error occurred.');
        }
    }
);
export const updateBannerAds = createAsyncThunk(
    'auth/updateBannerAds',
    async ({ message, values, isForStation = false  }, { rejectWithValue, getState }) => {
        try {
            const { auth } = getState();
            const username = isForStation ? auth?.user?.attributes?.ownerEmail : auth.user?.attributes?.username;
            const url = isForStation ? ENDPOINTS.USERS.UPDATE_STATION_INFO : ENDPOINTS.USERS.UPDATE_PROFILE;
            console.log(values, 'values')
            const requestData = {
                data: {
                    type: 'user',
                    id: username,
                    attributes: values
                },
            };
            const response = await axiosInstance.put(url, requestData);
            if (response.status === 204) {
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                });
                const { stationGroup } = getState().auth.user.attributes;
                if (stationGroup) {
                    await purgeCloudflareCache(stationGroup);
                }
                return values;
            } else {
                return rejectWithValue('Failed to update profile.');
            }
        } catch (error) {
            console.log(error.message)
            return rejectWithValue(error.response?.data?.message || 'An error occurred.');
        }
    }
);

// Action to clear errors
export const clearError = createAction('auth/clearError');

export const updateAdminPassword = createAsyncThunk(
    'auth/updateAdminPassword',
    async ({ message, values, targetGroupAdmin = false }, { rejectWithValue, getState }) => {
        try {
            const { auth } = getState();
            const targetUser = targetGroupAdmin ? (auth?.otherUser || auth?.user) : auth?.user;
            const username = targetUser?.attributes?.username;
            const rawGroupAdminToken = targetGroupAdmin
                ? (localStorage.getItem('groupAdminAuthToken') || sessionStorage.getItem('groupAdminAuthToken'))
                : null;
            const groupAdminToken = (rawGroupAdminToken && rawGroupAdminToken !== 'undefined' && rawGroupAdminToken !== 'null')
                ? rawGroupAdminToken
                : null;
            if (targetGroupAdmin && !groupAdminToken) {
                return rejectWithValue("Group admin token missing. Please login again.");
            }
            const requestData = {
                data: {
                    type: 'user',
                    id: username,
                    attributes: values
                },
            };
            const response = await axiosInstance.post(
                ENDPOINTS.ADMIN.UPDATE_PASSWORD,
                requestData,
                groupAdminToken ? { headers: { Authorization: `JWT ${groupAdminToken}` } } : undefined
            );

            if (response.status === 204) {
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                });

                localStorage.removeItem('alias');
                localStorage.removeItem('authToken');
                localStorage.removeItem('rfx:jwt');

                sessionStorage.setItem('logoutMessage', 'You have to login again after changing the password.');
                window.location.href = '/';


                return requestData.data.attributes;
            } else {
                return rejectWithValue('Failed to update profile.');
            }
        } catch (error) {
            console.log('Password update error:', error);

            // Handle JSON API error format
            if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                const errorMessage = error.response.data.errors[0]?.detail || error.response.data.errors[0]?.title || 'An error occurred.';
                console.log(errorMessage, 'errorMessage')
                return rejectWithValue(errorMessage);
            }

            // Handle regular error format
            return rejectWithValue(error.response?.data?.message || error.message || 'An error occurred.');
        }
    }
);


export const handleUpload = createAsyncThunk(
    'upload/handleUpload',
    async ({ files, options = {}, setProgress }, { dispatch, rejectWithValue, getState }) => {
        try {
            const state = getState();
            const username = state.auth.user?.attributes?.username; // Get username from state
            const blobs = await readBlobs(files);
            setProgress(30);
            const response = await axiosInstance.post(ENDPOINTS.FILES.UPLOAD_BANNER, {
                data: {
                    type: 'upload',
                    attributes: {
                        meta: options.meta || { name: 'logo' },
                        files: files.map((file, idx) => ({
                            type: idx === 0 ? 'original' : 'cropped',
                            contentType: file.type,
                            contentLength: file.size,
                            md5Hash: blobs[idx].md5,
                        })),

                        username,
                    },
                },
            });
            setProgress(60);
            const data = response.data.data;
            const filesData = data.attributes.files;
            await Promise.all(
                data.links.map((link, index) => (
                    axiosInstance.put(link, blobs[index].arrayBuffer, {
                        headers: {
                            'Content-MD5': filesData[index].md5Hash,
                            'Cache-Control': 'public,max-age=31536000',
                            'Content-Type': filesData[index].contentType,
                            'X-Goog-Acl': 'public-read',
                        },
                    })
                ))
            );
            setProgress(80);


            // dispatch(progressModal({ title: 'Processing image' }));

            // Step 5: Trigger Download Process
            const { uploadId } = data.attributes;
            const downloadResponse = await dispatch(download({ uploadId, username }));
            setProgress(90);

            // Step 6: Extract & Return Cropped Image URL
            const [, cropped] = downloadResponse.payload.data.attributes.urls;
            return cropped;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const handleUploadStationLogo = createAsyncThunk(
    'upload/handleUploadStationLogo',
    async ({ files, options = {}, setProgress }, { dispatch, rejectWithValue, getState }) => {
        try {
            const state = getState();
            const username = state.auth.user?.attributes?.username; // Get username from state
            const blobs = await readBlobs(files);
            setProgress(30);
            const response = await axiosInstance.post(ENDPOINTS.FILES.UPLOAD_BANNER, {
                data: {
                    type: 'upload',
                    attributes: {
                        meta: options.meta || { name: 'logo' },
                        files: files.map((file, idx) => ({
                            type: idx === 0 ? 'original' : 'cropped',
                            contentType: file.type,
                            contentLength: file.size,
                            md5Hash: blobs[idx].md5,
                        })),

                        username,
                    },
                },
            });
            setProgress(60);
            const data = response.data.data;
            const filesData = data.attributes.files;
            await Promise.all(
                data.links.map((link, index) => (
                    axiosInstance.put(link, blobs[index].arrayBuffer, {
                        headers: {
                            'Content-MD5': filesData[index].md5Hash,
                            'Cache-Control': 'public,max-age=31536000',
                            'Content-Type': filesData[index].contentType,
                            'X-Goog-Acl': 'public-read',
                        },
                    })
                ))
            );
            setProgress(80);


            // dispatch(progressModal({ title: 'Processing image' }));

            // Step 5: Trigger Download Process
            const { uploadId } = data.attributes;
            const downloadResponse = await dispatch(download({ uploadId, username }));
            setProgress(90);

            // Step 6: Extract & Return Cropped Image URL
            const [,cropped] = downloadResponse.payload.data.attributes.urls;
            return cropped;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const handleUploadProfilePhoto = createAsyncThunk(
    'upload/handleUploadProfilePhoto',
    async ({ files, options = {}, setProgress }, { dispatch, rejectWithValue, getState }) => {
        try {
            const state = getState();
            const username = state.auth.user?.attributes?.username; // Get username from state
            const blobs = await readBlobs(files);
            setProgress(30);
            const response = await axiosInstance.post(ENDPOINTS.FILES.UPLOAD_BANNER, {
                data: {
                    type: 'upload',
                    attributes: {
                        meta: options.meta || { name: 'avatar' },
                        files: files.map((file, idx) => ({
                            type: idx === 0 ? 'original' : 'cropped',
                            contentType: file.type,
                            contentLength: file.size,
                            md5Hash: blobs[idx].md5,
                        })),

                        username,
                    },
                },
            });
            setProgress(60);
            const data = response.data.data;
            const filesData = data.attributes.files;
            await Promise.all(
                data.links.map((link, index) => (
                    axiosInstance.put(link, blobs[index].arrayBuffer, {
                        headers: {
                            'Content-MD5': filesData[index].md5Hash,
                            'Cache-Control': 'public,max-age=31536000',
                            'Content-Type': filesData[index].contentType,
                            'X-Goog-Acl': 'public-read',
                        },
                    })
                ))
            );
            setProgress(80);

            // Step 5: Trigger Download Process
            const { uploadId } = data.attributes;
            const downloadResponse = await dispatch(download({ uploadId, username }));
            setProgress(90);

            // Step 6: Extract & Return Cropped Image URL
            const [,cropped] = downloadResponse.payload.data.attributes.urls;
            return cropped.trim();
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);



const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        loginSuccess: (state, action) => {
            state.isAuthenticated = true;
            state.user = action.payload;
            state.loading = false;
        },
        loginFailure: (state, action) => {
            state.isAuthenticated = false;
            state.loading = false;
            state.error = action.payload;
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.otherUser = null;
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem(OTHER_USER_STORAGE_KEY);
            state.error = null;
            window.location.href = "/auth/login";
        },
        updateAds: (state, action) => {
            try {
                if (state.user && state.user.attributes) {
                    // Ensure ads object exists
                    if (!state.user.attributes.ads) {
                        state.user.attributes.ads = {};
                    }

                    // Ensure banners array exists
                    if (!Array.isArray(state.user.attributes.ads.banners)) {
                        state.user.attributes.ads.banners = [];
                    }

                    // Finally push
                    state.user.attributes.ads.banners.push(action.payload);
                }
            } catch (error) {
                console.log(error.message);
            }
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.data;
                localStorage.setItem('authToken', action.payload.meta.jwt);
                sessionStorage.setItem('authToken', action.payload.meta.jwt);
                state.isAuthenticated = true;

                const roles = action.payload.data?.attributes?.roles || [];
                if (roles.includes('group_admin')) {
                    state.otherUser = action.payload.data;
                    try {
                         localStorage.setItem('groupAdminAuthToken', action.payload.meta.jwt);
                        sessionStorage.setItem(OTHER_USER_STORAGE_KEY, JSON.stringify(action.payload.data));
                    } catch (e) { /* ignore */ }
                }

                state.userRoleInfo = getUserRoleInfo(action.payload.data);
                state.isRoot = state.userRoleInfo.isRoot ? 1 : 0;
                state.error = null;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchCurrentUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                state.error = null;

                const roles = action.payload?.attributes?.roles || [];
                if (roles.includes('group_admin')) {
                    state.otherUser = action.payload;
                    try {
                        // Keep existing group-admin token; /admins/me payload usually has no meta.jwt
                        const existingGroupToken = localStorage.getItem('groupAdminAuthToken') || sessionStorage.getItem('groupAdminAuthToken');
                        if (existingGroupToken && existingGroupToken !== 'undefined' && existingGroupToken !== 'null') {
                            localStorage.setItem('groupAdminAuthToken', existingGroupToken);
                            sessionStorage.setItem('groupAdminAuthToken', existingGroupToken);
                        }
                        sessionStorage.setItem(OTHER_USER_STORAGE_KEY, JSON.stringify(action.payload));
                    } catch (e) { /* ignore */ }
                } else {
                    try {
                        const stored = sessionStorage.getItem(OTHER_USER_STORAGE_KEY);
                        if (stored) state.otherUser = JSON.parse(stored);
                    } catch (e) { /* ignore */ }
                }

                state.userRoleInfo = getUserRoleInfo(action.payload);
                state.isRoot = state.userRoleInfo.isRoot ? 1 : 0;
            })
            .addCase(fetchCurrentUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.status = 'failed';
                state.error = action.payload;
                state.user = null;
                state.otherUser = null;
                localStorage.removeItem('authToken');
                sessionStorage.removeItem('authToken');
                sessionStorage.removeItem(OTHER_USER_STORAGE_KEY);
            })
            .addCase(impersonateStationUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.user;
                state.isAuthenticated = true;
                state.error = null;
                state.userRoleInfo = getUserRoleInfo(action.payload.user);
                state.isRoot = state.userRoleInfo.isRoot ? 1 : 0;
                // otherUser unchanged (group_admin) so they can switch station later
            })
            .addCase(impersonateStationUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                const targetGroupAdmin = action.payload?.__targetGroupAdmin;
                const updatedAttributes = { ...(action.payload || {}) };
                delete updatedAttributes.__targetGroupAdmin;

                if (targetGroupAdmin && state.otherUser) {
                    state.otherUser.attributes = {
                        ...state.otherUser.attributes,
                        ...updatedAttributes,
                    };
                } else if (state.user) {
                    state.user.attributes = {
                        ...state.user.attributes,
                        ...updatedAttributes,
                    };
                }
            })
            .addCase(updateVideoAds.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.attributes = {
                        ...state.user.attributes,
                        videoAds: action.payload.videoAds || { videos: [] }
                    };
                }
            })
            .addCase(updateAudioAds.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.attributes = {
                        ...state.user.attributes,
                        audioAds: action.payload.audioAds || { audios: [] }
                    };
                }
            })
            .addCase(updateBannerAds.fulfilled, (state, action) => {
                state.loading = false;
                if (state.user) {
                    state.user.attributes = {
                        ...state.user.attributes,
                        ads: action.payload.ads || { ads: {} }
                    };
                }
            })
            .addCase(apiLimitSetByAdmin.fulfilled, (state, action) => {
                state.loading = false;
                state.apiLimit = action.payload;
                state.error = null;
            })
            .addCase(apiLimitSetByAdmin.rejected, (state, action) => {
                state.loading = false;
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(updateAdminPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.status = 'succeeded';
                state.error = null;
            })
            .addCase(updateAdminPassword.rejected, (state, action) => {
                state.loading = false;
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(clearError, (state) => {
                state.error = null;
            })
            .addCase(updateTwitter.fulfilled, (state, action) => {
                state.loading = false;
                state.user.attributes = {
                    ...state.user.attributes,
                    twitter: null
                };
                state.error = null;
            })
            .addCase(connectTwitter.fulfilled, (state, action) => {
                state.loading = false;
                state.user.attributes = {
                    ...state.user.attributes,
                    twitter: action.payload
                };
                state.error = null;
            })
            .addCase(connectTwitter.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateTwitter.rejected, (state, action) => {
                state.loading = false;
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(updateApiLimit.fulfilled, (state, action) => {
                state.loading = false;
                state.apiLimit.limitSetByAdmin = action.payload.limit;
                state.error = null;
            })
            .addCase(updateApiLimit.rejected, (state, action) => {
                state.loading = false;
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

export const { loginRequest, loginSuccess, loginFailure, logout, updateAds } = authSlice.actions;

export default authSlice.reducer;
